/** HTTP header Playwright and BFFs send to opt out of Customer.io / Segment for a request chain. */
export const E2E_HEADER = 'x-e2e-test'

/** Set to `true` to send real martech events during local BFF dev (opt-out of suppression). */
export const MARTECH_ENABLED_ENV = 'MARTECH_ENABLED'

export type MartechEnv = Record<string, string | boolean | undefined>

export function isMartechExplicitlyEnabled(env: MartechEnv = process.env): boolean {
  const value = env[MARTECH_ENABLED_ENV]
  if (value === undefined || value === null || value === '') {
    return false
  }

  return String(value).toLowerCase() === 'true'
}

export interface ShouldSuppressMartechInput {
  /** Value of the incoming `x-e2e-test` header, if any. */
  e2eHeader?: string | null
  /** True on Nuxt/Vite dev server or Nest `NODE_ENV=development`. */
  isLocalDevServer?: boolean
  env?: MartechEnv
}

/**
 * Returns true when Customer.io / Segment / similar sinks should not send events.
 * Local dev and e2e are suppressed by default; set MARTECH_ENABLED=true to re-enable.
 */
export function shouldSuppressMartech(input: ShouldSuppressMartechInput): boolean {
  // x-e2e-test is a per-request safety override and must win over local opt-in envs.
  if (input.e2eHeader?.toLowerCase() === 'true') {
    return true
  }

  if (isMartechExplicitlyEnabled(input.env)) {
    return false
  }

  if (input.isLocalDevServer) {
    return true
  }

  return false
}

/**
 * Headers to attach on outbound service calls (orchestration, etc.) when martech should be suppressed.
 */
export function buildMartechOutboundHeaders(input: ShouldSuppressMartechInput): Record<string, string> {
  if (!shouldSuppressMartech(input)) {
    return {}
  }

  // Propagate to orchestration so communication activities skip SNS → Customer.io Track publishes.
  return martechSuppressionHeaders(true)
}

/** When suppression is already decided, format the header for downstream orchestration calls. */
export function martechSuppressionHeaders(suppress: boolean): Record<string, string> {
  if (!suppress) {
    return {}
  }

  // Orchestration reads this header in activities and skips SNS → Customer.io Track.
  return { [E2E_HEADER]: 'true' }
}
